import {
  DEFAULT_ATTRS,
  DEFAULT_MAX_LENGTH_BY_TYPE,
  LENGTH_LIMITS,
  MODE_APPOINTMENT,
  MODE_CONTACT,
  PRESCREENER_CATEGORIES,
  PRESCREENER_CATEGORY_KEYS,
  QUESTION_NAMES,
  QUESTION_TYPES,
  SUBTEXT_KEYS,
  VALIDATION_MESSAGE_KEYS,
} from './constants';
import { validateFileUpload } from './validation-helpers';
import stateGroups from 'ember-simplepractice/utils/state-groups';

export default class QuestionProcessor {
  store;
  startAt;
  numItems;
  formType;
  prescreener;
  questionRecord;
  excludeCategories;
  includeCategories;
  categoryCountMap;

  constructor(props) {
    let { startAt, formType, numItems, prescreener, includeCategories, excludeCategories } = props;

    this.formType = formType;
    this.categoryCountMap = {};
    this.startAt = startAt || 0;
    this.prescreener = prescreener;
    this.includeCategories = includeCategories || [];
    this.excludeCategories = excludeCategories || [];
    this.numItems = isNaN(numItems) ? Number.MAX_SAFE_INTEGER : numItems;
  }

  get questions() {
    return this.prescreener.questionRecord?.questions;
  }

  get categories() {
    return [...new Set(this.#visibleQuestions.map(q => q.category))];
  }

  get groupedQuestions() {
    let grouped = this.groupBy(this.effectiveQuestions, q => q.categoryKey);

    return Object.entries(grouped).map(([categoryKey, questions]) => ({
      categoryKey,
      questions,
    }));
  }

  groupBy(array, callbackFn) {
    return array.reduce((result, item) => {
      const key = String(callbackFn(item));

      if (!Object.prototype.hasOwnProperty.call(result, key)) {
        result[key] = [];
      }

      result[key].push(item);

      return result;
    }, Object.create(null));
  }

  get effectiveQuestions() {
    return this.#visibleQuestions.map(question => {
      let type = this.#normalizeType(question);
      let options = this.#optionsFor(question, type);
      let validation = this.#validationFor(question, type);
      let subtext = this.#getSpecialCaseSubtext(question, validation);
      let typeaheadMetadata = this.#typeaheadMetadata(type, question.name);
      let categoryKey = PRESCREENER_CATEGORY_KEYS[question.category];
      let isClientTypeAware = question.category === PRESCREENER_CATEGORIES.DEMOGRAPHICS;

      return {
        ...DEFAULT_ATTRS,
        ...question,
        ...validation,
        ...typeaheadMetadata,
        isClientTypeAware,
        categoryKey,
        subtext,
        options,
        type,
      };
    });
  }

  get #visibleQuestions() {
    return this.questions?.filter(question => this.#shouldShowQuestion(question)) ?? [];
  }

  #shouldShowQuestion(question) {
    if (!this.#shouldShowCategory(question.category)) return false;
    let { isContactFormEnabled, isAppointmentFormEnabled } = this.prescreener;
    let { showOnContactForm, showOnAppointmentForm } = question ?? {};
    let { formType } = this;

    const questionCanShow = Boolean(question.show);
    const canShowOnContact =
      showOnContactForm && formType === MODE_CONTACT && questionCanShow && isContactFormEnabled;
    const canShowOnAppointment =
      showOnAppointmentForm &&
      formType === MODE_APPOINTMENT &&
      questionCanShow &&
      isAppointmentFormEnabled;
    const willShow = canShowOnContact || canShowOnAppointment;

    if (!willShow) return false;
    if (!this.#categoryBoundCheck(question.category)) return false;

    return willShow;
  }

  #categoryBoundCheck(category) {
    if (!this.startAt && this.numItems === Number.MAX_SAFE_INTEGER) return true;

    if (typeof this.categoryCountMap[category] === 'undefined') {
      this.categoryCountMap[category] = -1;
    }

    const categoryIndex = this.categoryCountMap[category] + 1;
    this.categoryCountMap[category] = categoryIndex;

    const categoryCount = categoryIndex + 1;

    return categoryIndex >= this.startAt && categoryCount <= this.numItems;
  }

  #shouldShowCategory(categoryName) {
    const hasIncludes = this.includeCategories?.length > 0;
    const hasExcludes = this.excludeCategories?.length > 0;

    if (hasIncludes && hasExcludes) return false;

    if (hasExcludes) {
      return !this.excludeCategories.includes(categoryName);
    }

    if (hasIncludes) {
      return this.includeCategories.includes(categoryName);
    }

    return true;
  }

  #normalizeType(question) {
    let { type, name } = question;

    switch (name) {
      case QUESTION_NAMES.GENDER:
      case QUESTION_NAMES.STATE:
        return QUESTION_TYPES.SELECT;
      case QUESTION_NAMES.PREFERRED_CLINICIAN:
        return QUESTION_TYPES.TYPEAHEAD;
      default:
        return type;
    }
  }

  #optionsFor(question, type) {
    let { name } = question;
    let opts = Array.isArray(question.options) ? question.options : [];
    let processedOptions;

    switch (name) {
      case QUESTION_NAMES.PREFERRED_CLINICIAN:
        processedOptions = this.#getClinicianOptions();
        break;
      case QUESTION_NAMES.STATE:
        processedOptions = this.#stateOptions;
        break;
      default:
        processedOptions = opts;
    }

    if (type === QUESTION_TYPES.SELECT) {
      return this.#processSelectOptions(processedOptions, name);
    }

    return processedOptions;
  }

  get #stateOptions() {
    const formatNonApplicable = option => option || 'N/A';
    return stateGroups.map(({ name, options }) => ({
      label: name,
      options: options.map(formatNonApplicable),
    }));
  }

  #typeaheadMetadata(questionType, questionName) {
    if (questionType !== QUESTION_TYPES.TYPEAHEAD) {
      return {};
    }

    switch (questionName) {
      case QUESTION_NAMES.PREFERRED_CLINICIAN:
        return {
          typeaheadModelName: 'clinician',
          typeaheadOptionProperty: 'fullName',
          typeaheadFields: 'firstName,lastName,middleName,suffix',
          typeaheadSearchParam: 'clinicianName',
          typeaheadIsPreloaded: true,
        };
      default:
        return null;
    }
  }

  #processSelectOptions(options, questionName) {
    const normalizeOption = option => {
      return typeof option === 'string' ? { value: option, label: option } : option;
    };

    let processedOptions = options.map(opt => {
      if (opt.options) {
        return {
          ...opt,
          options: opt.options.map(normalizeOption),
        };
      }
      return normalizeOption(opt);
    });

    return this.#applySelectSpecialCases(processedOptions, questionName);
  }

  #applySelectSpecialCases(options, questionName) {
    if (questionName === QUESTION_NAMES.PREFERRED_CLINICIAN && options.length === 1) {
      options[0].selected = true;
    }

    return options;
  }

  #getClinicianOptions() {
    let nameProcessor = clinician => {
      let label = clinician.fullName || [clinician.firstName, clinician.lastName].join(' ');
      return {
        value: clinician.id,
        label,
      };
    };
    return this.prescreener.store.peekAll('clinician').map(nameProcessor);
  }

  #validationFor(question, type) {
    const { name, required, hint, maxLength: questionMaxLength } = question;
    let requiredBoolean = Boolean(required);

    let maxLength = Number(questionMaxLength);
    if (isNaN(maxLength) || !maxLength) {
      maxLength = DEFAULT_MAX_LENGTH_BY_TYPE[type] || LENGTH_LIMITS.TEXT_AREA_MAX;
    }

    const validationMessageKeys = this.#getValidationMessageKeys({ name, type, hint, maxLength });
    const customValidator = this.#getCustomValidator(question, type);

    return {
      required: requiredBoolean,
      maxLength,
      validationMessageKeys,
      customValidator,
    };
  }

  #getSpecialCaseSubtext(question, validation) {
    const { name, type } = question;
    let subtext = SUBTEXT_KEYS[name] ?? '';
    if (!subtext && type === QUESTION_TYPES.TEXT) {
      subtext = validation.validationMessageKeys.maxLength;
    }
    return subtext;
  }

  #getCustomValidator(question, type) {
    if (type === QUESTION_TYPES.FILE) {
      return (value, model) => {
        model.prescreener = this.prescreener;
        return validateFileUpload(value, model);
      };
    }
    return null;
  }

  #getValidationMessageKeys(question) {
    const { name, type, hint } = question;

    if (name === QUESTION_NAMES.STATE) {
      return {
        required: VALIDATION_MESSAGE_KEYS.state,
      };
    }

    if (name === QUESTION_NAMES.DATE_OF_BIRTH) {
      return {
        required: VALIDATION_MESSAGE_KEYS.dateOfBirth,
      };
    }

    if (name === QUESTION_NAMES.DRIVERS_LICENSE) {
      return {
        required: VALIDATION_MESSAGE_KEYS.driversLicense,
      };
    }

    if (type === QUESTION_TYPES.MULTISELECT) {
      return {
        required: hint
          ? VALIDATION_MESSAGE_KEYS.multiselect.hint
          : VALIDATION_MESSAGE_KEYS.multiselect.default,
      };
    }

    return {
      required: VALIDATION_MESSAGE_KEYS[type] || VALIDATION_MESSAGE_KEYS.string,
      maxLength: VALIDATION_MESSAGE_KEYS.maxLength,
    };
  }
}
